import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import noDataImage from '@src/assets/images/new-no-data.svg';
import NotificationItem from './NotificationItem';
import {
  StyledNotificationList,
  StyledNotificationEmpty,
  StyledDivider,
} from './index.style';

const INDICATOR_SIZE = 52;
const PULL_THRESHOLD = 72;
const MAX_PULL = 110;
const SNAP_BACK = 'transform 0.35s cubic-bezier(0.23, 1, 0.32, 1)';

// Rubber-band: fast near zero, slows toward max
const applyResistance = (x) => MAX_PULL * (1 - Math.exp((-0.5 * x) / MAX_PULL));

const NotificationList = ({
  notifications = [],
  loading = false,
  hasMore = false,
  onLoadMore,
  onItemClick,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const observer = useRef();
  const listRef = useRef(null);
  const onRefreshRef = useRef(onRefresh);

  const touchStartY = useRef(0);
  const touchStartX = useRef(0);
  const gestureDirection = useRef(null); // null | 'vertical' | 'horizontal'
  const isPullingRef = useRef(false);
  const rafId = useRef(null);

  const [pullProgress, setPullProgress] = useState(0); // 0–1
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) onLoadMore();
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, onLoadMore],
  );

  const applyListTransform = (y, withTransition = false) => {
    const el = listRef.current;
    if (!el) return;
    el.style.transition = withTransition ? SNAP_BACK : 'none';
    el.style.transform = `translateY(${y}px)`;
  };

  useEffect(() => {
    if (!onRefresh) return undefined;
    const el = listRef.current;
    if (!el) return undefined;

    const resetState = () => {
      touchStartY.current = 0;
      touchStartX.current = 0;
      gestureDirection.current = null;
      isPullingRef.current = false;
    };

    const handleTouchStart = (e) => {
      if (e.touches.length > 1) {
        resetState();
        return;
      }
      // Always reset so stale values don't leak across touches
      resetState();
      if (el.scrollTop > 0) return;
      touchStartY.current = e.touches[0].clientY;
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
      if (e.touches.length > 1) return;
      if (!touchStartY.current) return;

      const deltaY = e.touches[0].clientY - touchStartY.current;
      const deltaX = e.touches[0].clientX - touchStartX.current;
      const dist = Math.sqrt(deltaY * deltaY + deltaX * deltaX);

      // Commit gesture direction after 10px touch slop
      if (!gestureDirection.current && dist > 10) {
        const angle = Math.abs(Math.atan2(deltaY, deltaX) * (180 / Math.PI));
        gestureDirection.current = angle > 60 ? 'vertical' : 'horizontal';
      }

      if (gestureDirection.current !== 'vertical') return;
      if (el.scrollTop > 0) return; // user scrolled down mid-gesture
      if (deltaY <= 0) return; // scrolling down, not pulling

      e.preventDefault();
      isPullingRef.current = true;

      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        const translated = applyResistance(deltaY);
        applyListTransform(translated);
        setPullProgress(Math.min(translated / PULL_THRESHOLD, 1));
      });
    };

    const handleTouchEnd = (e) => {
      cancelAnimationFrame(rafId.current);

      if (!isPullingRef.current) {
        resetState();
        return;
      }

      const deltaY = e.changedTouches[0].clientY - touchStartY.current;
      const translated = applyResistance(deltaY);

      if (translated >= PULL_THRESHOLD && onRefreshRef.current) {
        // Hold at indicator height while refreshing
        applyListTransform(INDICATOR_SIZE, true);
        setPullProgress(1);
        setIsPullRefreshing(true);
        onRefreshRef.current();
        setTimeout(() => {
          applyListTransform(0, true);
          setIsPullRefreshing(false);
          setPullProgress(0);
        }, 1200);
      } else {
        applyListTransform(0, true);
        setPullProgress(0);
      }

      resetState();
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      cancelAnimationFrame(rafId.current);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onRefresh]);

  if (!loading && notifications.length === 0) {
    return (
      <StyledNotificationEmpty>
        <img src={noDataImage} alt="" className="empty-image" />
        <span className="empty-text">{t('notificationEmpty')}</span>
      </StyledNotificationEmpty>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Indicator sits above the list, revealed when list translates down */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: INDICATOR_SIZE,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: 'none',
        }}
      >
        <CircularProgress
          size={24}
          variant={isPullRefreshing ? 'indeterminate' : 'determinate'}
          value={pullProgress * 100}
          sx={{
            opacity: pullProgress,
            transform:
              pullProgress >= 1 && !isPullRefreshing
                ? 'rotate(180deg)'
                : 'none',
            transition: 'transform 0.2s ease, opacity 0.15s ease',
          }}
        />
      </Box>

      {/* Scroll container — translate down to reveal indicator above */}
      <StyledNotificationList
        ref={listRef}
        sx={{ overscrollBehavior: 'contain', willChange: 'transform' }}
      >
        {notifications.map((notification, index) => {
          const isLast = index === notifications.length - 1;
          return (
            <React.Fragment key={notification.id}>
              <Box ref={isLast ? lastItemRef : null}>
                <NotificationItem
                  notification={notification}
                  onClick={onItemClick}
                />
              </Box>
              {!isLast && <StyledDivider />}
            </React.Fragment>
          );
        })}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </StyledNotificationList>
    </Box>
  );
};

export default NotificationList;
